import { useQuery } from "@apollo/client";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listStores } from "../graphql/queries/listStores";

const DEBOUNCE_MS = 400;

export const useStores = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterData, setFilterData] = useState({});
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debouncedFilters, setDebouncedFilters] = useState({});

  // Debounce search term
  const debouncedSetSearch = useRef(
    debounce((val) => setDebouncedSearch(val), DEBOUNCE_MS)
  ).current;

  useEffect(() => {
    debouncedSetSearch(searchTerm);
  }, [searchTerm, debouncedSetSearch]);

  // Debounce filter data
  const debouncedSetFilters = useRef(
    debounce((val) => setDebouncedFilters(val), DEBOUNCE_MS)
  ).current;

  useEffect(() => {
    debouncedSetFilters(filterData);
  }, [filterData, debouncedSetFilters]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
      debouncedSetFilters.cancel();
    };
  }, [debouncedSetSearch, debouncedSetFilters]);

  // Build metadataFilters from debounced values using { path, value } format
  const filters = useMemo(() => {
    const { name, retailer, country, state, city, postcode, category } =
      debouncedFilters;
    const metadataFilters = [];
    if (name?.trim()) metadataFilters.push({ path: "name", value: name.trim() });
    if (retailer?.trim())
      metadataFilters.push({ path: "retailer", value: retailer.trim() });
    if (country?.trim())
      metadataFilters.push({ path: "country", value: country.trim() });
    if (state?.trim()) metadataFilters.push({ path: "state", value: state.trim() });
    if (city?.trim()) metadataFilters.push({ path: "city", value: city.trim() });
    if (postcode?.trim())
      metadataFilters.push({ path: "postcode", value: postcode.trim() });

    const hasMetadata = metadataFilters.length > 0;
    const hasCategory = category?.trim()?.length > 0;

    if (!hasMetadata && !hasCategory) return undefined;

    return {
      ...(hasMetadata ? { metadataFilters } : {}),
      ...(hasCategory ? { category: category.trim() } : {}),
    };
  }, [debouncedFilters]);

  const { data, loading, error, refetch } = useQuery(listStores, {
    variables: { filters },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const allStores = useMemo(() => {
    if (!data?.listKeywordGroups) return [];

    return data.listKeywordGroups
      .map((group) => ({
        id: group.id,
        name: group.name,
        slug: group.slug,
        type: group.type,
        deleted: group.deleted,
        imageUrl: group.imageUrl,
        keywords: group.keywords,
        category: group.category,
        metadata: group.metadata,
        _searchName: group.name.toLowerCase(),
        _searchStoreName: (group.metadata?.name || "").toLowerCase(),
        _searchStoreId: (group.metadata?.store_id || "").toLowerCase(),
        _searchRetailer: (group.metadata?.retailer || "").toLowerCase(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  // Filtered stores (local search only, server handles metadata/category filters)
  const stores = useMemo(() => {
    const searchLower = debouncedSearch.toLowerCase().trim();
    if (!searchLower) return allStores;
    return allStores.filter(
      (store) =>
        store._searchName.includes(searchLower) ||
        store._searchStoreName.includes(searchLower) ||
        store._searchStoreId.includes(searchLower) ||
        store._searchRetailer.includes(searchLower)
    );
  }, [allStores, debouncedSearch]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e?.target?.value ?? e ?? "");
  }, []);

  const handleFilterChange = useCallback((newFilterData) => {
    setFilterData(newFilterData);
  }, []);

  return {
    stores,
    allStores,
    loading,
    error,
    searchTerm,
    filterData,
    refetch,
    handlers: {
      handleSearchChange,
      handleFilterChange,
    },
  };
};
